import React, { useState, useEffect } from 'react';
import { Typography, Paper, Box } from '@mui/material';
import { DataGrid, useGridApiRef } from '@mui/x-data-grid';
import { Select, MenuItem, FormControl, Pagination, PaginationItem } from '@mui/material';
import { GridFooterContainer } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import { styled } from '@mui/system';
import apiClient from '../../api/apiClient'; // API client import
import Config from '../../Config'; // apiUrl 추가
import { getAccessToken } from '../../utils/token';
import CustomColumnSortedAscendingIcon from '../../components/CustomColumnSortedAscendingIcon';
import CustomColumnSortedDescendingIcon from '../../components/CustomColumnSortedDescendingIcon';

const apiUrl = Config.apiUrl;

function CustomPagination(props) {
    const pageCount = Math.ceil(props.totalElements / props.size);
    const rowsPerPage = props.size;

    const handlePageChange = (event, value) => {
        props.onPageChange(value - 1); // DataGrid는 0-based page, Pagination은 1-based page
    };

    const handlePageSizeChange = (event) => {
        props.onPageSizeChange(parseInt(event.target.value, 10));
    };

    return (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0 16px', width: '100%', marginBottom: '25px' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="body1">Showing</Typography>
                <FormControl variant="outlined" sx={{ mx: 1, minWidth: 78, width: 78, height: 38 }}>
                    <Select value={rowsPerPage} onChange={handlePageSizeChange}
                        disableUnderline
                        sx={{ fontSize: '0.875rem', height: '100%', paddingTop: '8px', paddingBottom: '8px' }}>
                        {[10, 20, 30, 50].map((size) => (
                            <MenuItem key={size} value={size}>
                                {size}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <Typography variant="body1">of {props.totalElements}</Typography>
            </Box>
            {props.totalElements > 0 && (
                <Pagination
                    count={pageCount}
                    page={props.page + 1}
                    onChange={handlePageChange}
                    siblingCount={1}
                    boundaryCount={1}
                    shape="rounded"
                    color="primary"
                    variant='outlined'
                    renderItem={(item) => (
                        <PaginationItem
                            {...item}
                            sx={{
                                '&.Mui-selected': {
                                    backgroundColor: '#1976d2',
                                    color: '#fff',
                                },
                                backgroundColor: item.selected ? 'initial' : '#e0e0e0',
                                color: item.selected ? '#fff' : '#8b909a', // 선택되지 않았을 때 글자색 설정
                                border: 'none',
                                width: '28px',    // 가로 크기 설정
                                height: '28px',   // 세로 크기 설정
                                margin: '1px',    // 박스 간 간격 설정 (전체 간격이 2px이므로 양쪽에 1px씩)
                            }}
                        />
                    )}
                />
            )}
        </Box>
    );
}

const NoRowsOverlay = styled('div')({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    fontSize: '1.5rem',
    color: '#888',
    flexDirection: 'column',
});

function CustomFooter(props) {
    return (
        <GridFooterContainer sx={{ borderTop: 'none', color: '#8b909a' }}>
            <CustomPagination
                page={props.page}
                size={props.size}
                totalElements={props.totalElements}
                onPageChange={props.onPageChange}
                onPageSizeChange={props.onPageSizeChange}
            />
        </GridFooterContainer>
    );
}

const UpdateHistoryPanel = ({ rowId }) => {
    const [rows, setRows] = useState([]);
    const { t } = useTranslation('console');
    const apiRef = useGridApiRef();
    const getRowHeight = (params) => 47;

    const [loading, setLoading] = useState(true);

    console.log('UpdateHistoryPanel received rowId:', rowId?.id);

    useEffect(() => {
        const fetchUpdateHistoryInfo = async () => {
            setLoading(true);
            try {
                const token = getAccessToken();
                const response = await apiClient.get(`${apiUrl}/console/devices/${rowId.id}/update-histories`, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });

                // API 응답이 배열인지 페이지네이션 객체인지 확인
                const dataArray = Array.isArray(response.data)
                    ? response.data
                    : (response.data.content || response.data);

                const mappedRows = dataArray.map((item, index) => ({
                    id: item.id || index,
                    module: item.moduleName,
                    complete: item.completeTime,
                    elapsed: item.elapsedTime,
                    old_version: item.oldVersion,
                    new_version: item.newVersion,
                    method: item.method,
                }));

                setRows(mappedRows);
            } catch (error) {
                console.error('Error fetching update history info:', error);
            } finally {
                setLoading(false);
            }
        };

        if (rowId && rowId.id) {
            fetchUpdateHistoryInfo();
        }
    }, [rowId]);

    const columns = [
        { field: 'id', headerName: `${t('device_list.update_history_tab.column.no')}`, flex: 1, minWidth: 70, headerAlign: 'center', align: 'center' },
        { field: 'module', headerName: `${t('device_list.update_history_tab.column.module')}`, flex: 1.5, minWidth: 100, headerAlign: 'center', align: 'center' },
        { field: 'complete', headerName: `${t('device_list.update_history_tab.column.complete')}`, flex: 2, minWidth: 100, headerAlign: 'center', align: 'center' },
        { field: 'elapsed', headerName: `${t('device_list.update_history_tab.column.elapsed')}`, flex: 1, minWidth: 100, headerAlign: 'center', align: 'center' },
        { field: 'old_version', headerName: `${t('device_list.update_history_tab.column.old_version')}`, flex: 1, minWidth: 100, headerAlign: 'center', align: 'center' },
        { field: 'new_version', headerName: `${t('device_list.update_history_tab.column.new_version')}`, flex: 1, minWidth: 100, headerAlign: 'center', align: 'center' },
        { field: 'method', headerName: `${t('device_list.update_history_tab.column.method')}`, flex: 1, minWidth: 100, headerAlign: 'center', align: 'center' },
    ];

    return (
        <Paper
            sx={{
                width: '900px',
                height: '700px',
                borderRadius: '10px',
                boxShadow: '0px 4px 24px rgba(0, 0, 0, 0.1)',
            }}
        >
            <Box display="flex" alignItems="center" sx={{
                paddingLeft: '26px',
                paddingTop: '26px',
            }}>
                <img src="/icon-update.png" alt="update icon" style={{ width: '30px', height: '30px', marginRight: '8px' }} />
                <Typography variant="h6" sx={{ fontSize: '20px', fontWeight: 'bold', color: '#303468' }}>
                    {t('device_list.update_history_tab.title')}
                </Typography>
            </Box>
            <Box sx={{ height: '620px', paddingTop: '20px', paddingLeft: '18px', paddingRight: '18px' }}>
                <DataGrid
                    rows={rows}
                    columns={columns}
                    apiRef={apiRef}
                    getRowId={(row) => row.id}
                    getRowHeight={getRowHeight}
                    headerHeight={54}
                    loading={loading}
                    pageSizeOptions={[10, 20, 30, 50]}
                    slots={{
                        footer: () => {
                            const paginationModel = apiRef.current.state.pagination.paginationModel;
                            return (
                                <CustomFooter
                                    page={paginationModel.page}
                                    size={paginationModel.pageSize}
                                    totalElements={rows.length}
                                    onPageChange={(newPage) => {
                                        apiRef.current.setPage(newPage);
                                    }}
                                    onPageSizeChange={(newSize) => {
                                        apiRef.current.setPageSize(newSize);
                                    }}
                                />
                            );
                        },
                        noRowsOverlay: () => (loading ? null : (
                            <NoRowsOverlay>
                                <img src="nodata.png" alt="No data" />
                            </NoRowsOverlay>
                        )),
                        columnSortedAscendingIcon: CustomColumnSortedAscendingIcon,
                        columnSortedDescendingIcon: CustomColumnSortedDescendingIcon,
                    }}
                    initialState={{
                        pagination: { paginationModel: { pageSize: 10 } },
                    }}
                    sx={{
                        '&, [class^=MuiDataGrid]': {
                            borderLeft: 'none',
                            borderRight: 'none',
                            borderBottom: 'none',
                        },
                        '& .MuiDataGrid-columnHeaders div[role="row"]': {
                            backgroundColor: '#f9fafd',
                        },
                        '& .MuiDataGrid-columnHeaderTitle': {
                            textAlign: 'center',
                            display: 'flex',
                            justifyContent: 'center',
                            width: '100%',
                            fontSize: '12px',
                            fontWeight: '800',
                            color: '#7d7d7d',
                        },
                        '& .MuiDataGrid-cell': {
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            textAlign: 'center',
                            fontSize: '12px',
                            color: '#494949',
                            height: '47px',
                            backgroundColor: '#ffffff',
                        },
                        '& .MuiDataGrid-cellContent': {
                            width: '100%',
                        },
                        '& .MuiDataGrid-footerContainer': {
                            display: 'flex',
                            justifyContent: 'center',
                            marginTop: '30px',
                            backgroundColor: '#ffffff',
                        },
                        borderBottomLeftRadius: '14px',
                        borderBottomRightRadius: '14px',
                        overflow: 'hidden',
                        backgroundColor: '#ffffff',
                    }}
                />
            </Box>
        </Paper>
    );
};

export default UpdateHistoryPanel;